import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { 
  TrendingUp, 
  Award, 
  Briefcase, 
  Calendar,
  MapPin,
  Euro,
  Building,
  FileText
} from 'lucide-react';
import api from '../services/api';
import StatsCard from '../components/StatsCard';
import TenderCard from '../components/TenderCard';
import CompanyCard from '../components/CompanyCard';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard: React.FC = () => {
  const [selectedComune, setSelectedComune] = useState<string>('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  // Fetch stats (using public endpoint)
  const { data: statsResponse, isLoading: statsLoading, error: statsError } = useQuery(
    ['publicStats'],
    () => api.getPublicStats(),
    { retry: false, onError: () => {} }
  );
  const stats = statsResponse?.data;

  // Fetch tenders (using public endpoint)
  const { data: tendersResponse, isLoading: tendersLoading, error: tendersError } = useQuery(
    ['publicTenders'],
    () => api.getPublicTenders(10),
    { retry: false, onError: () => {} }
  );
  const tenders = tendersResponse?.data;

  // Fetch top companies (mock data for now)
  const { data: topCompanies, error: companiesError } = useQuery(
    'topCompanies',
    () => Promise.resolve([]),
    { retry: false, onError: () => {} }
  );

  // Fetch analytics data (mock data for now)
  const { data: yearlyData, error: yearlyError } = useQuery(
    'yearlyAnalytics',
    () => Promise.resolve([]),
    { retry: false, onError: () => {} }
  );

  const { data: typeData, error: typeError } = useQuery(
    'typeAnalytics',
    () => Promise.resolve([]),
    { retry: false, onError: () => {} }
  );

  const { data: statusData, error: statusError } = useQuery(
    'statusAnalytics',
    () => Promise.resolve([]),
    { retry: false, onError: () => {} }
  );

  const { data: comuniData, error: comuniError } = useQuery(
    'comuniStats',
    () => Promise.resolve([]),
    { retry: false, onError: () => {} }
  );

  // Chart configurations
  const yearChartConfig = {
    labels: Array.isArray(yearlyData) ? yearlyData.map(d => d.year) : ['2023', '2024'],
    datasets: [
      {
        label: 'Numero Bandi',
        data: Array.isArray(yearlyData) ? yearlyData.map(d => d.count) : [0, 0],
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
      },
    ],
  };

  const typeChartConfig = {
    labels: Array.isArray(typeData) ? typeData.map(d => d.tipo) : ['Forniture', 'Lavori', 'Servizi'],
    datasets: [
      {
        label: 'Bandi per Tipo',
        data: Array.isArray(typeData) ? typeData.map(d => d.count) : [0, 0, 0],
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(251, 146, 60, 0.7)',
          'rgba(139, 92, 246, 0.7)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const statusChartConfig = {
    labels: Array.isArray(statusData) ? statusData.map(d => d.stato) : ['Attivi', 'Scaduti', 'Aggiudicati'],
    datasets: [
      {
        label: 'Stato Bandi',
        data: Array.isArray(statusData) ? statusData.map(d => d.count) : [0, 0, 0],
        backgroundColor: [
          'rgba(34, 197, 94, 0.7)',
          'rgba(251, 191, 36, 0.7)',
          'rgba(239, 68, 68, 0.7)',
          'rgba(156, 163, 175, 0.7)',
        ],
      },
    ],
  };

  const amountByYearConfig = {
    labels: Array.isArray(yearlyData) ? yearlyData.map(d => d.year) : ['2023', '2024'],
    datasets: [
      {
        label: 'Importo Totale (€)',
        data: Array.isArray(yearlyData) ? yearlyData.map(d => d.total_amount) : [0, 0],
        backgroundColor: 'rgba(16, 185, 129, 0.2)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Dashboard Bandi Pubblici
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Monitoraggio in tempo reale dei bandi e appalti comunali
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Comune
            </label>
            <select
              value={selectedComune}
              onChange={(e) => setSelectedComune(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Tutti i comuni</option>
              {Array.isArray(comuniData) ? comuniData.map((comune) => (
                <option key={comune.id} value={comune.id}>
                  {comune.nome} ({comune.provincia})
                </option>
              )) : null}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Data Inizio
            </label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Data Fine
            </label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Totale Bandi"
          value={stats?.total_tenders || 0}
          icon={<FileText className="h-6 w-6" />}
          change="+12%"
          trend="up"
          color="blue"
        />
        <StatsCard
          title="Valore Totale"
          value={`€${(stats?.total_value || 0).toLocaleString('it-IT')}`}
          icon={<Euro className="h-6 w-6" />}
          change="+8%"
          trend="up"
          color="green"
        />
        <StatsCard
          title="Bandi Attivi"
          value={stats?.active_tenders || 0}
          icon={<TrendingUp className="h-6 w-6" />}
          change="+5"
          trend="up"
          color="yellow"
        />
        <StatsCard
          title="Aziende"
          value={stats?.total_companies || 0}
          icon={<Building className="h-6 w-6" />}
          change="+3%"
          trend="up"
          color="purple"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Bandi per Anno */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Bandi per Anno
          </h3>
          <Bar data={yearChartConfig} options={{
            responsive: true,
            plugins: {
              legend: { display: false },
            },
            scales: {
              y: { beginAtZero: true },
            },
          }} />
        </div>

        {/* Bandi per Tipo */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Distribuzione per Tipo
          </h3>
          <Doughnut data={typeChartConfig} options={{
            responsive: true,
            plugins: {
              legend: { position: 'bottom' as const },
            },
          }} />
        </div>

        {/* Importi per Anno */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Importi per Anno
          </h3>
          <Line data={amountByYearConfig} options={{
            responsive: true,
            plugins: {
              legend: { display: false },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    return '€' + value.toLocaleString('it-IT');
                  },
                },
              },
            },
          }} />
        </div>

        {/* Stati Bandi */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Stati Bandi
          </h3>
          <Doughnut data={statusChartConfig} options={{
            responsive: true,
            plugins: {
              legend: { position: 'bottom' as const },
            },
          }} />
        </div>
      </div>

      {/* Recent Tenders and Top Companies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tenders */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Bandi Recenti
            </h3>
            <a
              href="/tenders"
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Vedi tutti →
            </a>
          </div>
          <div className="space-y-3">
            {Array.isArray(tenders?.data) ? tenders.data.slice(0, 5).map((tender) => (
              <TenderCard key={tender.id} tender={tender} compact />
            )) : null}
          </div>
        </div>

        {/* Top Companies */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Top Aziende
            </h3>
            <a
              href="/companies"
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Vedi tutte →
            </a>
          </div>
          <div className="space-y-3">
            {Array.isArray(topCompanies) ? topCompanies.slice(0, 5).map((company, index) => (
              <CompanyCard 
                key={company.id} 
                company={company} 
                rank={index + 1}
                compact 
              />
            )) : null}
          </div>
        </div>
      </div>

      {/* Comuni Stats */}
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Statistiche per Comune
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Comune
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Provincia
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Bandi Totali
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Bandi Attivi
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Importo Totale
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {Array.isArray(comuniData) ? comuniData.map((comune) => (
                <tr key={comune.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                      {comune.nome}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                    {comune.provincia}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                    {comune.total_tenders || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                    <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {comune.active_tenders || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                    €{comune.total_value?.toLocaleString('it-IT') || '0'}
                  </td>
                </tr>
              )) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;